import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Flex,
  Input,
  Layout,
  Space,
  Table,
  TableProps
} from "antd";
import { ColumnsType } from "antd/es/table";
import { get, toNumber } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionContent from "src/components/common/ActionContent";
import ModalDeleteConfirm from "src/components/common/ModalDeleteConfirm";
import { apiCaller, cancelListToken } from "src/util/apiCaller";
import {
  getLocalStorage,
  isCheckTable,
  reDataForTable,
  setLocalStorage
} from "src/util/Commom";
import { MessageStatusError } from "src/util/MessageStatus";

interface Data {
  key: React.Key;
  tenPhongBan: string;
  maPhongBan: string;
}

const listCap = [
  { cap: 1, name: "Sở Ban Ngành" },
  { cap: 2, name: "Ngành dọc" },
  { cap: 3, name: "Huyện" },
  { cap: 4, name: "Xã" }
];

const PhongBanTable = ({ status }: any) => {
  const { isActive } = status;
  const [isLoadTable, setIsLoadTable] = useState<boolean>(false);
  const [datatable, setDatatable] = useState([]);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [isSearchClick, setIsSearchClick] = useState(false);
  const pageSize = 10;
  const navigate = useNavigate();
  useEffect(() => {
    if (isActive) {
      const state = getLocalStorage("dataFilter") || undefined;
      if (state) {
        setKeyword(state.keyword);
        setActivePage(state.activePage);
        getListData(state.activePage, state.keyword);
      } else {
        getListData(activePage, keyword);
      }
    } else {
      navigate("/error/401");
    }
    return () => {
      cancelListToken(["listPhongBan"]);
    };
  }, []);

  /**
   * getListData: gọi danh sách vai trò
   * @param {int} activePage
   * @param {string} keyword
   * @memberof PhongBan
   */
  const getListData = async (activePage: number, keyword: string) => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/phongbans?keyword=${keyword}&page=${activePage}&size=${pageSize}`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listPhongBan"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listPhongBan = get(response.data, "content", []);
        const data = reDataForTable(listPhongBan, activePage, pageSize);
        setDatatable(data as any);
        setTotal(get(listPhongBan, "totalElements", 0));
        setIsLoadTable(false);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTable(false);
      }
    } finally {
      setIsLoadTable(false);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "STT",
      dataIndex: "key",
      key: "key",
      width: "5%",
      align: "center"
    },
    { title: "Tên đơn vị", dataIndex: "tenDonVi", key: "tenDonVi" },
    {
      title: "Mã đơn vị",
      dataIndex: "maDinhDanh",
      key: "maDinhDanh",
      width: "15%"
    },
    {
      title: "Cấp đơn vị",
      dataIndex: "cap",
      key: "cap",
      width: "20%",
      render: (item) => renderCap(item)
    },
    {
      title: "Đơn vị",
      dataIndex: "isDonVi",
      key: "isDonVi",
      width: "10%",
      align: "center",
      render: (item) => isCheckTable(item)
    },

    {
      title: "Hành động",
      dataIndex: "",
      key: "x",
      align: "center",
      width: "10%",
      render: (item) => actionContent(item)
    }
  ];

  const renderCap = (cap: any) => {
    let capdv = "";
    switch (cap) {
      case 1:
        capdv = "Sở ban ngành";
        break;
      case 2:
        capdv = "Ngành dọc";
        break;
      case 3:
        capdv = "Huyện";
        break;
      case 4:
        capdv = "Xã";
        break;
      default:
    }
    return capdv;
  };

  /**
   * actionContent: hành động trên bảng
   * @param {object} item
   * @memberof PhongBan
   */
  const actionContent = (item: object) => {
    return (
      <ActionContent
        onDeleteItem={() => deleteItemFunc(item)}
        onEditItem={() => handleRedirect("edit", item)}
      />
    );
  };

  /**
   * deleteItemFunc: Xoá item theo item
   * @param {object} item
   * @memberof PhongBan
   */
  const deleteItemFunc = (item: object) => {
    ModalDeleteConfirm(deleteItemAction, item);
  };

  /**
   * deleteItemAction: Xóa item
   * @param {object} item
   * @memberof PhongBan
   */
  const deleteItemAction = async (item: any) => {
    const config = {
      url: `api/phongbans/${item.id}`,
      apiType: "DELETE",
      value: null,
      method: "DELETE",
      getName: "deleteListQuyen"
    };
    const response = await apiCaller(config);
    try {
      if (response?.status === 200 || response?.status === 201) {
        const dataPage = total - (activePage - 1) * pageSize;
        const page = dataPage <= 1 && total !== 1 ? activePage - 1 : activePage;
        getListData(page, keyword);
        setIsLoadTable(true);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTable(false);
      }
    } catch (error) {
      setIsLoadTable(false);
      // Xử lý lỗi khi lưu dữ liệu không thành công
    }
  };

  /**
   * handleRedirect: Chuyển trang thêm mới và chỉnh sửa
   * @param {string} type
   * @param {object} item
   * @memberof PhongBan
   */
  const handleRedirect = (type = "", item: any) => {
    let pathname = "/quan-tri/phong-ban-table";
    switch (type) {
      case "new":
        pathname += "/them-moi/";
        break;
      case "edit":
        pathname += `/${item.id}/chinh-sua/`;
        break;
      default:
        break;
    }
    navigate(pathname, { state: { itemData: item } });
    if (isSearchClick) {
      const content = {
        keyword,
        activePage,
        pageSize,
        pathname: "quan-tri/phong-ban-table"
      };
      setLocalStorage("dataFilter", content);
    }
  };

  /**
   * handleTableChange: Fetch dữ liệu dựa theo thay đổi trang
   * @param {object} pagination
   * @memberof PhongBan
   */
  const handleTableChange: TableProps["onChange"] = (pagination) => {
    const activePage = pagination.current as number;
    getListData(activePage, keyword);
    setActivePage(activePage);
  };

  /**
   * onChangeText: Thay đổi keyword tìm kiếm
   * @param {object} e
   * @memberof PhongBan
   */
  const onChangeText = (e: any) => {
    const keyword = e.target.value;
    setKeyword(keyword);
  };

  /**
   * handleSubmit: Thực hiện hành động tìm kiếm
   * @memberof PhongBan
   */
  const handleSubmit = () => {
    setIsSearchClick(true);
    getListData(activePage, keyword);
  };

  return (
    <Layout>
      <Flex justify="space-between" align="center">
        <h3 style={{ color: "black", fontSize: 20 }}>Phòng ban</h3>
        <Space>
          <Input
            className="heading-input-search"
            name="keyword"
            onPressEnter={handleSubmit}
            onChange={onChangeText}
            placeholder="Nhập từ khóa..."
            value={keyword}
            allowClear
            type="text"
          />
          <Button
            className="heading-button-search btn-success"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
          />
          <Button
            className="heading-button-addnew btn"
            type="primary"
            onClick={() => handleRedirect("new", null)}
            icon={<PlusOutlined style={{ marginRight: 3 }} />}
          >
            Thêm mới
          </Button>
        </Space>
      </Flex>
      <Card>
        <Table
          rowKey={"id"}
          bordered
          columns={columns}
          className="qti-table-wrapper gx-table-responsive"
          dataSource={datatable}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "qti-table-row-background" : ""
          }
          size="middle"
          pagination={{
            current: activePage,
            pageSize,
            total,
            showTotal: (total) =>
              pageSize <= total
                ? `Hiển thị ${pageSize} trong tổng ${total}`
                : `Tổng ${total}`
          }}
          loading={isLoadTable}
          onChange={handleTableChange}
          scroll={{ x: 700 }}
        />
      </Card>
    </Layout>
  );
};
export default PhongBanTable;
